import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import { StatusBadge } from '@/components/scan'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { scanService } from '@/lib/services/scanService'
import { writingAssistantService } from '@/lib/services/writingAssistantService'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { formatDateTime, formatEndDateTime } from '@/lib/utils/timeFormat'
import type { ScanListItem } from '@/lib/services/scanService'
import type { WritingAssistantListItem } from '@/lib/types/writing'
import type { BobAnalyse } from '@/lib/services/bobAnalyseService'
import type { ScanStatus } from '@/lib/types'
import {
  FileText,
  PenTool,
  Users,
  Briefcase,
  Plus,
  ArrowRight,
  Clock,
  Building2,
} from 'lucide-react'
import { useAuth } from '@/lib/hooks/useAuth'

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeCrisis } = useCrisisStore()
  const [scans, setScans] = useState<ScanListItem[]>([])
  const [writings, setWritings] = useState<WritingAssistantListItem[]>([])
  const [bobAnalyses, setBobAnalyses] = useState<BobAnalyse[]>([])
  const [totalBobAnalysesCount, setTotalBobAnalysesCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth()

  useEffect(() => {
    if (activeCrisis) {
      document.title = `Dashboard - ${activeCrisis.type} - BGCH Crisis Buddy`
    } else {
      document.title = `Dashboard - BGCH Crisis Buddy`
    }
  }, [activeCrisis])

  // Function to load dashboard data
  const loadDashboardData = async () => {
    if (!activeCrisis) {
      setIsLoading(false)
      return
    }

    try {
      const [scansResponse, writingsResponse, bobAnalysesResponse] = await Promise.all([
        scanService.getScans(activeCrisis.id),
        writingAssistantService.getWritingAssistants(activeCrisis.id),
        bobAnalyseService.listBobAnalyses(activeCrisis.id),
      ])

      // Get last 3 items sorted by date
      const sortedScans = [...scansResponse.data]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3)

      const sortedWritings = [...writingsResponse.data]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3)

      const sortedBobAnalyses = [...bobAnalysesResponse].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )

      // Store total count before slicing
      setTotalBobAnalysesCount(sortedBobAnalyses.length)

      setScans(sortedScans)
      setWritings(sortedWritings)
      // Take the last 3 (most recent) items, maintaining oldest-first order
      setBobAnalyses(sortedBobAnalyses.slice(-3))
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Initial load and polling
  useEffect(() => {
    if (!activeCrisis) {
      setIsLoading(false)
      return
    }

    // Initial load
    setIsLoading(true)
    loadDashboardData()

    // Poll every 5 seconds
    const intervalId = setInterval(() => {
      loadDashboardData()
    }, 5000)

    return () => {
      clearInterval(intervalId)
    }
  }, [activeCrisis])

  // Calculate quick stats

  const getWritingStatusBadge = (status: string) => {
    const statusMap: Record<string, { className: string; label: string }> = {
      ready: {
        className: 'bg-green-100 text-green-800 border-green-300',
        label: t('writing.status.ready'),
      },
      processing: {
        className: 'bg-blue-100 text-blue-800 border-blue-300',
        label: t('writing.status.processing'),
      },
      queued: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        label: t('writing.status.queued'),
      },
      failed: {
        className: 'bg-red-100 text-red-800 border-red-300',
        label: t('writing.status.failed'),
      },
    }
    const config = statusMap[status] || {
      className: 'bg-gray-100 text-gray-800 border-gray-300',
      label: status,
    }
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}
      >
        {config.label}
      </span>
    )
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return `${diffMins} ${t('scan.minutenGeleden')}`
    if (diffHours < 24) return `${diffHours} ${t('scan.uurGeleden')}`
    return `${diffDays} ${t('scan.dagenGeleden')}`
  }

  // Helper for navigation with type safety workaround
  const navigateTo = (
    pathOrOptions: string | { to: string; search?: Record<string, string | undefined> }
  ) => {
    // Use navigate with type assertion for paths not strictly typed in router
    if (typeof pathOrOptions === 'string') {
      navigate({ to: pathOrOptions } as unknown as Parameters<typeof navigate>[0])
    } else {
      navigate({ to: pathOrOptions.to, search: pathOrOptions.search } as unknown as Parameters<
        typeof navigate
      >[0])
    }
  }

  const userPermissions = (user && user.permissions) || []
  const canUpdateScans = userPermissions.includes('scans.update')

  return (
    <Layout>
      {/* Crisis Info Header */}
      {activeCrisis && (
        <div className="container mx-auto px-4 md:px-6 mt-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="md:text-4xl font-bold text-foreground">
                Dashboard | {activeCrisis.type} - {activeCrisis.location}
              </h1>
              <p className="text-base md:text-xl text-muted-foreground">
                {t('dashboard.welkomBericht')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Tool Cards Grid */}
      <div className="container mx-auto px-4 py-6 md:px-6 md:py-12">
        {isLoading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"
                aria-hidden="true"
              ></div>
              <p className="text-muted-foreground">{t('dashboard.status.laden')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Media Scans Card */}
            {userPermissions.includes('scans.list') && (
              <Card>
                <CardHeader className="pb-3 md:pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
                      <CardTitle className="text-lg">{t('dashboard.tools.mediaScans')}</CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigateTo('/scan')}>
                      {t('dashboard.tools.allesbekijken')}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {scans.length === 0 && userPermissions.includes('scans.create') ? (
                    <div className="text-center py-2 text-muted-foreground">
                      <p className="mb-3">{t('dashboard.tools.geenItems')}</p>
                      <Button size="sm" onClick={() => navigateTo('/scan')}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('dashboard.tools.nieuweMaken')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {scans.map(scan => {
                          // Determine if the scan is accessible to the current user
                          const isAccessible = scan.status === 'confirmed' || canUpdateScans
                          const scanIsClickable = isAccessible && (scan.status === 'draft' || scan.status === 'confirmed')

                          return (
                          <div
                            key={scan.id}
                            className={`flex items-center justify-between p-3 rounded-lg transition-colors shadow-sm border ${scanIsClickable ? 'hover:bg-muted/50 cursor-pointer' : ''} ${!isAccessible ? 'cursor-not-allowed bg-muted/30' : ''}`}
                            onClick={() => {
                              if (scanIsClickable) {
                                navigateTo(`/scan/detail/${scan.id}`)
                              }
                            }}
                            title={!isAccessible ? 'U hebt geen toestemming om deze analyse te bekijken' : ''}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {scan.timeFrame?.from && scan.timeFrame?.to
                                  ? `${formatDateTime(scan.timeFrame.from)} - ${formatEndDateTime(scan.timeFrame.from, scan.timeFrame.to)}`
                                  : scan.title || t('scan.naamlozeAnalyse')}
                              </p>
                              {scan.keywords && scan.keywords.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex flex-wrap gap-1 items-center">
                                    <div className="text-xs font-medium text-muted-foreground me-1">
                                      Zoekterm:
                                    </div>
                                    <code className="text-xs font-mono text-foreground bg-muted/50 px-2 py-1 rounded border border-border truncate max-w-full">
                                      {scan.keywords.length === 0 ? (
                                        `(${scan.location})`
                                      ) : (
                                        <>
                                          ({scan.location}) AND (
                                          {scan.keywords.slice(0).join(' OR ')})
                                        </>
                                      )}
                                    </code>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="ml-2">
                              <StatusBadge status={scan.status as ScanStatus} />
                            </div>
                          </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Communication Approach Card */}
            {userPermissions.includes('bob.list') && (
              <Card>
                <CardHeader className="pb-3 md:pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-primary" aria-hidden="true" />
                      <CardTitle className="text-lg">
                        {t('dashboard.tools.communicatieaanpak')}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {bobAnalyses.length === 0 && userPermissions.includes('bob.create') ? (
                    <div className="text-center py-2 text-muted-foreground">
                      <p className="mb-3">{t('dashboard.tools.geenItems')}</p>
                      <Button size="sm" onClick={() => navigateTo('/bob')}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('dashboard.tools.nieuweMaken')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {bobAnalyses.map((bob, index) => {
                          // bobAnalyses is the last 3 items from a sorted (oldest-first) list
                          // So the first item in bobAnalyses is at position (total - 3), second at (total - 2), third at (total - 1)
                          // Since array numbering starts at 1, we add 1: (total - bobAnalyses.length + index + 1)
                          const analyseNumber =
                            totalBobAnalysesCount - bobAnalyses.length + index + 1
                          const createdDate = new Date(bob.createdAt)
                          const formattedDateTime = formatDateTime(createdDate)

                          return (
                            <div
                              key={bob.id}
                              className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shadow-sm border"
                              onClick={() =>
                                navigateTo({ to: '/bob', search: { selectedId: bob.id } })
                              }
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  Strategie {analyseNumber} | {formattedDateTime}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Clock className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {formatRelativeTime(bob.createdAt)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-2">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                    bob.status === 'completed' || bob.status === 'ready'
                                      ? 'bg-green-100 text-green-800 border-green-300'
                                      : bob.status === 'processing'
                                        ? 'bg-blue-100 text-blue-800 border-blue-300'
                                        : bob.status === 'queued'
                                          ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                          : 'bg-gray-100 text-gray-800 border-gray-300'
                                  }`}
                                >
                                  {bob.status === 'completed' || bob.status === 'ready'
                                    ? t('bob.status.completed')
                                    : bob.status === 'processing'
                                      ? t('bob.status.processing')
                                      : bob.status === 'queued'
                                        ? t('bob.status.queued')
                                        : bob.status === 'failed'
                                          ? t('bob.status.failed')
                                          : bob.status}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Writing Assistant Card */}
            {userPermissions.includes('writing.list') && (
              <Card>
                <CardHeader className="pb-3 md:pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PenTool className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">
                        {t('dashboard.tools.schrijfassistent')}
                      </CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigateTo('/writing')}>
                      {t('dashboard.tools.allesbekijken')}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {writings.length === 0 && userPermissions.includes('writing.create') ? (
                    <div className="text-center py-2 text-muted-foreground">
                      <p className="mb-3">{t('dashboard.tools.geenItems')}</p>
                      <Button size="sm" onClick={() => navigateTo('/writing')}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('dashboard.tools.nieuweMaken')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {writings.map(writing => (
                          <div
                            key={writing.id}
                            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shadow-sm border"
                            onClick={() =>
                              navigateTo({ to: '/writing', search: { selectedId: writing.id } })
                            }
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{writing.contentType}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeTime(writing.createdAt)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-2">{getWritingStatusBadge(writing.status)}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contacts Card */}
            {userPermissions.includes('contacts.list') && (
              <Card>
                <CardHeader className="pb-3 md:pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" aria-hidden="true" />
                      <CardTitle className="text-lg">{t('dashboard.tools.contacten')}</CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigateTo('/contacts')}>
                      {t('dashboard.tools.allesbekijken')}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('dashboard.tools.contactenBeschrijving')}
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {/* Hulpdiensten en ketenpartners Tab */}
                    <div
                      className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shadow-sm border border-orange-200 bg-orange-50/30"
                      onClick={() =>
                        navigateTo({ to: '/contacts', search: { tab: 'Hulpdiensten' } })
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {t('contacts.tabs.hulpdiensten')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('contacts.tabs.hulpdienstenBeschrijving')}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>

                    {/* Overzicht betrokken partijen Tab */}
                    <div
                      className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shadow-sm border border-blue-200 bg-blue-50/30"
                      onClick={() => navigateTo({ to: '/contacts', search: { tab: 'Overzicht' } })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {t('contacts.tabs.betrokkenPartijen')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('contacts.tabs.betrokkenPartijenBeschrijving')}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
